<!-- src/Components/MediaUploadQueue.vue -->
<!-- src/Components/MediaUploadQueue.vue -->
<template>
  <div
    v-if="hasAnyVisibleUploads"
    class="fixed bottom-4 right-4 w-96 bg-white shadow-2xl rounded-xl border z-40"
  >
    <!-- HEADER -->
    <div
      :class="[
        'flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-zinc-100',
        collapseAll ? 'rounded-xl' : 'rounded-t-xl'
      ]"
    >
      <h4 class="text-sm font-semibold text-zinc-700">
        {{ visibleUploads.length }} uploads
      </h4>

      <div class="flex items-center gap-3">
        <!-- Collapse All -->
        <button
          title="Collapse or expand uploads"
          @click="collapseAll = !collapseAll"
          class="rounded-full bg-zinc-100 text-gray-800 p-1 hover:bg-gray-200 hover:text-gray-800 focus:outline-none font-semibold"
        >
          <ChevronDownIcon v-if="!collapseAll" class="h-6 w-6" />
          <ChevronUpIcon v-else class="h-6 w-6" />
        </button>

        <!-- Close -->
        <button
          type="button"
          title="Close upload panel"
          class="rounded-full bg-zinc-100 text-gray-800 p-1 hover:bg-gray-200 hover:text-gray-800 focus:outline-none font-semibold"
          @click="handleClose"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
      </div>
    </div>

    <!-- BLUE STATUS BAR (Google Drive style) -->
    <div
      v-if="activeUpload && !collapseAll"
      class="flex items-center justify-between px-3 py-2 bg-[#e8f2ff] border-b border-blue-200"
    >
      <!-- ETA -->
      <div class="text-[12px] text-zinc-600">
        <span v-if="activeUpload.etaSeconds <= 5">
          Less than a minute left
        </span>
        <span v-else>
          {{ Math.ceil(activeUpload.etaSeconds / 60) }} minutes left
        </span>
      </div>

      <!-- CANCEL BUTTON -->
      <button
        @click="uploadStore.cancelUpload(activeUpload.id)"
        class="text-[12px] text-blue-600 hover:text-red-500"
      >
        Cancel
      </button>
    </div>

    <!-- LIST -->
    <div
      v-if="!collapseAll"
      class="max-h-64 overflow-y-auto p-3 space-y-2 bg-transparent rounded-b-xl"
    >
      <div
        v-for="u in visibleUploads"
        :key="u.id"
        class="rounded-lg p-2"
      >
        <div class="flex justify-between items-center">
          <!-- LEFT: ICON + NAME -->
          <div class="flex items-center gap-2">
            <component
              :is="fileIcon(u.fileName).icon"
              :class="['h-6 w-6', fileIcon(u.fileName).color]"
            />
            <span class="text-[14px] font-base text-zinc-700 truncate max-w-[150px]">
              {{ u.fileName }}
            </span>
          </div>

          <!-- RIGHT: STATUS -->
          <div class="flex items-center">
            <!-- DONE -->
            <CheckCircleIcon
              v-if="isDone(u)"
              class="h-7 w-7 text-green-500"
            />

            <!-- ERROR -->
            <div v-else-if="u.status === 'error'" class="flex items-center gap-1">
              <ExclamationCircleIcon class="h-6 w-6 text-red-500" />
            </div>

            <!-- CANCELED -->
            <div
              v-else-if="u.status === 'canceled'"
              class="text-[11px] text-red-500 font-medium"
            >
              Cancelled By You
            </div>

            <!-- PROGRESS CIRCLE -->
            <svg
              v-else
              class="h-6 w-6"
              viewBox="0 0 36 36"
            >
              <path
                class="text-zinc-200"
                stroke="currentColor"
                stroke-width="5"
                fill="none"
                d="M18 2a16 16 0 1 1 0 32 16 16 0 1 1 0-32"
              />
              <path
                class="text-blue-500 transition-all duration-300"
                stroke="currentColor"
                stroke-width="5"
                fill="none"
                stroke-linecap="round"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset(u.progress ?? 0)"
                d="M18 2a16 16 0 1 1 0 32 16 16 0 1 1 0-32"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useMediaUploadStore } from "@/stores/mediaUploadStore";

import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/vue/24/outline";

import {
  CheckCircleIcon,
  PhotoIcon,
  FilmIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/24/solid";

const uploadStore = useMediaUploadStore();
const { uploads } = storeToRefs(uploadStore);
const { clearFinished } = uploadStore;

// Only show uploads that came from the media library (default origin)
const visibleUploads = computed(() =>
  uploads.value.filter(u => !u.origin || u.origin === 'mediaLibrary')
);

const hasAnyVisibleUploads = computed(() => visibleUploads.value.length > 0);

const collapseAll = ref(false);

// BLUE BAR shows the FIRST active upload (like Google Drive)
const activeUpload = computed(() =>
  uploads.value.find(u => u.status === "uploading")
);

// Icon colors
const PLATFORMA_BLUE = "text-[#2D8CFF]";

const fileIcon = (fileName) => {
  const ext = fileName.toLowerCase();

  if (ext.match(/\.(mp4|mkv|mov|avi|wmv)$/))
    return { icon: FilmIcon, color: "text-red-500" };

  if (ext.match(/\.(png|jpg|jpeg|gif|webp|avif)$/))
    return { icon: PhotoIcon, color: "text-purple-500" };

  if (ext.match(/\.(pdf|txt|doc|docx|xls|xlsx|csv)$/))
    return { icon: DocumentTextIcon, color: PLATFORMA_BLUE };

  return { icon: DocumentTextIcon, color: PLATFORMA_BLUE };
};

// When upload reaches 100% treat as complete
const isDone = (u) => {
  const s = (u.status || "").toLowerCase();
  return s === "done" || s === "success" || u.progress >= 100;
};

// Progress circle math
const radius = 16;
const circumference = 2 * Math.PI * radius;

const dashOffset = (pct) => {
  const safe = Math.min(Math.max(pct, 0), 100);
  return circumference - (safe / 100) * circumference;
};

const emitClose = () => {
  uploads.value = [];
};

const handleClose = () => {
  clearFinished();
  emitClose();
};
</script>

<style scoped>
[v-cloak] {
  display: none;
}
</style>
